import { fetchApi } from "@/lib/api/index.ts";
import { User } from "@/lib/api/user.ts";
import { useQuery } from "@tanstack/react-query";

export type ScoresDetails = {
  id: string;
  created_at: string;
  updated_at: string;
  mentor: User;
  mentor_id: string;
  snapshot_date: string;
  rolling_window_days: number;
  total_active_teens: number;
  total_points_last_window: number;
  total_mentees_last_window: number;
  base_score: string;
  average_satisfaction_score: string;
  satisfaction_multiplier: string;
  caseload_multiplier: string;
  final_score: string;
  scoring_algorithm_version: string;
  first_week_engagement_count: string;
  month1_retention_count: string;
  month3_retention_count: string;
  total_monthly_eligible_teens: number;
  newly_eligible_teens: number;
  total_monthly_deactivated_teens: number;
  newly_deactivated_teens: number;
};
export type SnapshotDetails = {
  id: string;
  engaged_this_month: boolean;
  first_week_engagement: boolean;
  month1_retention: boolean;
  month3_retention: boolean;
  first_week_unique_days: number;
  average_rating: string;
  points_earned: number;
  teen: User;
  mentorship_id: string;
  mentorship_start_date: string;
};

export type MentorScoresResponse = {
  scores: ScoresDetails[];
  isLastPage: boolean;
};

export type MentorScoresSnapshotResponse = {
  details: SnapshotDetails[];
  isLastPage: boolean;
};

export interface DashboardStats {
  totalMentors: number;
  totalBonusPayouts: number;
  avgBonusPerMentor: number;
  totalPaidEngagements: number;
}

export type PayoutData = {
  mentor: string;
  total_bonus_teens: number;
  current_financial_bonus: number | string;
  name: string;
};

export const useGetMentorshipScores = (
  page: number,
  limit: number,
  search?: string,
  customFilter?: string,
  group?: string,
  status?: string,
  year?: string,
  month?: string,
  unique?: boolean,
  mentorId?: string,
) => {
  return useQuery<{ scores: ScoresDetails[]; isLastPage: boolean }>({
    queryKey: [
      "mentor-scoring",
      page,
      limit,
      search,
      customFilter,
      group,
      status,
      year,
      month,
      unique,
      mentorId,
    ],
    queryFn: async () => {
      const response = await fetchApi<MentorScoresResponse>({
        url: "/v2/mentor/scoring",
        method: "GET",
        params: {
          page,
          limit,
          search,
          customFilter,
          group,
          status,
          year,
          month,
          unique: unique ? "true" : undefined,
          mentorId,
        },
      });

      if (!response.data?.scores) {
        throw new Error("No scores found");
      }

      return {
        scores: response.data.scores,
        isLastPage: response.data.isLastPage,
      };
    },
  });
};

export const useGetMentorshipSnapshotDetails = (
  snapshotId: string,
  page: number,
  limit: number,
) => {
  return useQuery<{ details: SnapshotDetails[]; isLastPage: boolean }>({
    queryKey: [
      "mentor-scoring-details",
      snapshotId,
      page,
      limit,
    ],
    queryFn: async () => {
      const response = await fetchApi<MentorScoresSnapshotResponse>({
        url: "/v2/mentor/scoring/details",
        method: "GET",
        params: {
          snapshotId,
          page,
          limit,
        },
      });

      if (!response.data?.details) {
        throw new Error("No scores found");
      }

      return {
        details: response.data.details,
        isLastPage: response.data.isLastPage,
      };
    },
  });
};

export const useGetMentorshipSnapshots = (
  page: number,
  limit: number,
  mentorIds: string[],
  search?: string,
  customFilter?: string,
  group?: string,
  status?: string,
  year?: string,
  month?: string,
  unique?: boolean,
) => {
  return useQuery<{ scores: ScoresDetails[] }>({
    queryKey: [
      "mentor-scoring-snapshots",
      page,
      limit,
      ...mentorIds,
      search,
      customFilter,
      group,
      status,
      year,
      month,
      unique,
    ],
    queryFn: async () => {
      // Fetch data for each mentor ID
      const allScores: ScoresDetails[] = [];

      const responses = await Promise.all(
        mentorIds.map(mentorId => 
          fetchApi<MentorScoresResponse>({
            url: "/v2/mentor/scoring",
            method: "GET",
            params: {
              page,
              limit,
              mentorId,
              search,
              customFilter,
              group,
              status,
              year,
              month,
              unique: unique ? "true" : undefined,
            },
          })
        )
      );

    responses.forEach(response => {
      if (response.data?.scores) {
        allScores.push(...response.data.scores);
      }
    });

      return {
        scores: allScores,
      };
    },
    enabled: mentorIds.length > 0,
  });
};

export const useGetMentorPayouts = (
  year: string,
  month: string,
) => {
  return useQuery<PayoutData[]>({
    queryKey: ["mentor-payouts", year, month],
    queryFn: async () => {
      const response = await fetchApi<PayoutData[]>({
        url: "/v2/admin/payouts",
        method: "GET",
        params: {
          year,
          month,
        },
      });

      if (!response.data) {
        throw new Error("No payout data found");
      }

      return response.data;
    },
  });
};


